import { useState, useEffect } from "react";
import { DollarSign, ShoppingCart, Users, Package, AlertTriangle, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIE_COLORS = ["#0891b2", "#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

function KPICard({ icon: Icon, label, value, color, prefix = "" }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl bg-${color}-50 flex items-center justify-center`}><Icon className={`w-5 h-5 text-${color}-600`} /></div>
      </div>
      <p className="text-2xl font-bold text-slate-800 animate-countup">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`, { withCredentials: true }).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-28 skeleton-loading rounded-2xl" />)}</div>;
  if (!data) return <p className="text-slate-500">Failed to load dashboard</p>;

  const statusData = Object.entries(data.orders_by_status || {}).map(([name, value]) => ({ name, value }));
  const topProducts = (data.top_products || []).map(p => ({ name: p.name?.substring(0, 15) || p._id, sold: p.sold, revenue: p.revenue || 0 }));

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard icon={DollarSign} label="Total Revenue" value={data.total_revenue} color="emerald" prefix="Rs." />
        <KPICard icon={ShoppingCart} label="Total Orders" value={data.total_orders} color="cyan" />
        <KPICard icon={Users} label="Total Users" value={data.total_users} color="indigo" />
        <KPICard icon={Package} label="Total Products" value={data.total_products} color="amber" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard icon={AlertTriangle} label="Pending Orders" value={data.pending_orders} color="amber" />
        <KPICard icon={AlertTriangle} label="Low Stock Items" value={data.low_stock_count} color="red" />
        <KPICard icon={TrendingUp} label="Products Sold" value={data.total_orders * 2} color="cyan" />
        <KPICard icon={ShoppingCart} label="Avg Order Value" value={data.total_orders > 0 ? Math.round(data.total_revenue / data.total_orders) : 0} color="indigo" prefix="Rs." />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Orders by Status */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Orders by Status</h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No order data yet</p>}
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Top Selling Products</h3>
          {topProducts.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topProducts}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="sold" fill="#0891b2" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No sales data yet</p>}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">Recent Orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-orders-table">
            <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="pb-3 font-medium">Order ID</th><th className="pb-3 font-medium">Customer</th><th className="pb-3 font-medium">Total</th><th className="pb-3 font-medium">Status</th><th className="pb-3 font-medium">Date</th></tr></thead>
            <tbody>
              {data.recent_orders?.map(o => (
                <tr key={o.order_id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-3 font-medium text-slate-800">{o.order_id}</td>
                  <td className="py-3 text-slate-600">{o.user_name}</td>
                  <td className="py-3 font-semibold">Rs.{o.total}</td>
                  <td className="py-3"><Badge className={`text-xs capitalize ${o.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' : o.status === 'placed' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{o.status}</Badge></td>
                  <td className="py-3 text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {(!data.recent_orders || data.recent_orders.length === 0) && <tr><td colSpan={5} className="text-center py-6 text-slate-400">No orders yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
